import React, { useMemo, memo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { tasksAtom, updateTaskAtom } from '../../../../atoms/v0/taskAtoms';
import { projectsAtom } from '../../../../atoms/v0/projectAtoms';
import { globalTaskEditAtom } from '../../../../atoms/v3/taskAtoms';
import { layoutModeAtom, activeTabAtom } from '../../../../atoms/v3/uiAtoms';
import { groupTasksByTime } from '../../../../utils/taskUtils';
import { Task } from '../../../../types/task';
import { TaskItem } from '../tasks/TaskItem';

interface RecentTasksSectionProps {
  className?: string;
}

/**
 * 最近要做的事情组件
 * 显示1-5个未完成任务，按照今天→昨天→明天的优先级排序
 */
const RecentTasksSectionComp: React.FC<RecentTasksSectionProps> = ({
  className = ''
}) => {
  const { t } = useTranslation(['v3/chat', 'v3/tasks']);
  const tasks = useAtomValue(tasksAtom);
  const projects = useAtomValue(projectsAtom);
  const updateTask = useSetAtom(updateTaskAtom);
  const setGlobalTaskEdit = useSetAtom(globalTaskEditAtom);
  const layoutMode = useAtomValue(layoutModeAtom);
  const activeTab = useAtomValue(activeTabAtom);

  // 如果是桌面端且在tasks页面，不显示此组件
  const shouldHide = layoutMode === 'desktop' && activeTab === 'tasks';

  // 创建项目映射
  const projectsMap = useMemo(() => {
    const map: Record<string, { id: string; title: string }> = {};
    projects.forEach(project => {
      map[project.id] = {
        id: project.id,
        title: project.title
      };
    });
    return map;
  }, [projects]);

  // 获取并排序未完成任务，并添加项目信息
  const recentTasks = useMemo(() => {
    // 筛选未完成任务
    const incompleteTasks = tasks.filter(task => !task.done);

    if (incompleteTasks.length === 0) {
      return [];
    }

    // 按时间分组
    const tasksByTime = groupTasksByTime(incompleteTasks);

    // 按优先级合并：今天 → 昨天 → 明天
    const prioritizedTasks: Task[] = [];

    // 添加今天的任务
    prioritizedTasks.push(...tasksByTime.today);

    // 如果不足5个，添加昨天的任务
    if (prioritizedTasks.length < 5) {
      const remainingSlots = 5 - prioritizedTasks.length;
      prioritizedTasks.push(...tasksByTime.yesterday.slice(0, remainingSlots));
    }

    // 如果还不足5个，添加明天的任务
    if (prioritizedTasks.length < 5) {
      const remainingSlots = 5 - prioritizedTasks.length;
      prioritizedTasks.push(...tasksByTime.tomorrow.slice(0, remainingSlots));
    }

    // 在每个时间组内按重要性排序并添加项目信息
    return prioritizedTasks.sort((a, b) => {
      // 优先级排序：vital > urgent > 普通
      if (a.vital && !b.vital) return -1;
      if (!a.vital && b.vital) return 1;
      if (a.urgent && !b.urgent) return -1;
      if (!a.urgent && b.urgent) return 1;

      // 按标题排序
      return a.title.localeCompare(b.title);
    }).slice(0, 5).map(task => {
      // 为每个任务添加项目信息
      if (task.projectId && projectsMap[task.projectId]) {
        return {
          ...task,
          project: projectsMap[task.projectId]
        };
      }
      return task;
    }); // 最多5个
  }, [tasks, projectsMap]);

  // 处理任务切换
  const handleToggleTask = (taskId: string, completed: boolean) => {
    updateTask({
      id: taskId,
      done: completed
    });
  };

  // 处理任务点击
  const handleTaskClick = (taskId: string) => {
    setGlobalTaskEdit({
      taskId: taskId,
      isOpen: true,
      triggerSource: 'hyperlink'
    });
  };

  // 处理项目点击
  const handleProjectClick = (projectId: string) => {
    // 可以添加导航到项目详情的逻辑
    console.log('Project clicked:', projectId);
  };

  // 如果应该隐藏或没有任务，不渲染组件
  if (shouldHide || recentTasks.length === 0) {
    return null;
  }

  return (
    <div className={`w-full max-w-2xl mx-auto mt-8 ${className}`}>
      {/* 标题 */}
      <div className="text-center mb-4">
        <h3 className="text-lg font-medium text-ios-black dark:text-white mb-1">
          {t('v3/chat:recentTasks.title', '最近要做的事情')}
        </h3>
        <p className="text-sm text-ios-gray dark:text-ios-dark-gray">
          {t('v3/chat:recentTasks.count', '{{count}} 个待处理任务', { count: recentTasks.length })}
        </p>
      </div>

      {/* 任务列表 */}
      <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg overflow-hidden shadow-ios dark:shadow-ios-dark">
        {recentTasks.map((task, index) => (
          <TaskItem
            key={task.id}
            task={task}
            onToggle={handleToggleTask}
            onClick={handleTaskClick}
            onProjectClick={task.projectId ? () => handleProjectClick(task.projectId!) : undefined}
            isFirst={index === 0}
            isLast={index === recentTasks.length - 1}
            hideProjectName={false}
            context="chat"
          />
        ))}
      </div>
    </div>
  );
};

export const RecentTasksSection = memo(RecentTasksSectionComp);
export default RecentTasksSection;
